import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { changeContractOptions, changeFormProblem, changeInput, emptyChangeForm, formFromChange, type ChangeForm } from '../lib/changes'
import { CHANGE_CAUSES, CHANGE_CAUSE_KEY, CHANGE_KINDS, CHANGE_KIND_KEY } from '../lib/labels'
import type { CostChange, CostChangeCause, CostChangeKind, CostContract } from '../types'

const NONE = 'none'

interface Props {
  open: boolean
  mode: 'create' | 'edit'
  change?: CostChange | null
  contracts: CostContract[]
  initialKind?: CostChangeKind
  initialContractId?: string | null
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the input; the dialog stays open on failure. */
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

const ChangeDialog: React.FC<Props> = ({ open, mode, change, contracts, initialKind, initialContractId, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<ChangeForm>(() => emptyChangeForm())
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Bumped every time the dialog opens; see the effect pair below.
  const [openGen, setOpenGen] = useState(0)

  useEffect(() => {
    if (!open) return
    const next = change ? formFromChange(change) : emptyChangeForm(initialKind, initialContractId ?? null)
    // The contract Select's options depend on `kind` (spec §1.4). Setting `kind` and a
    // pre-picked `contractId` in the same commit races Radix Select's hidden native-select
    // sync: the target SelectItem is still being mounted for the new `kind` when the sync
    // effect looks for a matching <option>, and it silently resets the value to ''. Settle
    // `kind` here (contractId null); the effect below applies contractId one commit later,
    // once that item has mounted.
    setForm({ ...next, contractId: null })
    setTouched(false)
    setSubmitting(false)
    setOpenGen((n) => n + 1)
    // Only on opening: a background reload must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open || openGen === 0) return
    const wanted = change ? change.contractId : (initialContractId ?? null)
    if (!wanted) return
    setForm((f) => (f.contractId === wanted ? f : { ...f, contractId: wanted }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openGen])

  // Edit mode keeps offering the change's own contract even once it stopped being ACTIVE (M6), so the Select shows
  // it instead of going blank; create mode has no stored contract to keep.
  const options = changeContractOptions(contracts, form.kind, change?.contractId ?? null)
  const problem = changeFormProblem(form)
  const contractOptional = form.kind === 'BUDGET_CHANGE' || form.kind === 'POTENTIAL'
  const setKind = (kind: CostChangeKind) => setForm((f) => ({
    ...f, kind, contractId: changeContractOptions(contracts, kind).some((c) => c.id === f.contractId) ? f.contractId : null,
  }))
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(changeInput(form, mode, change))
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }
  // Potential changes spec §8: optional for every kind, shown first for a potential change.
  const causeField = (
    <div className="space-y-1.5">
      <Label htmlFor="cc-change-cause">{t('CostControl_change_field_cause')}</Label>
      <Select value={form.cause ?? NONE} onValueChange={(v) => setForm((f) => ({ ...f, cause: v === NONE ? null : (v as CostChangeCause) }))}>
        <SelectTrigger id="cc-change-cause"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{t('CostControl_change_no_cause')}</SelectItem>
          {CHANGE_CAUSES.map((c) => <SelectItem key={c} value={c}>{t(CHANGE_CAUSE_KEY[c])}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(mode === 'create' ? 'CostControl_change_new' : 'CostControl_change_edit')}</DialogTitle>
          {mode === 'create' && <DialogDescription>{t('CostControl_change_dialog_hint')}</DialogDescription>}
        </DialogHeader>
        <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); submit() }}>
          {mode === 'create' && (
            <div className="space-y-1.5">
              <Label>{t('CostControl_change_field_kind')}</Label>
              <RadioGroup value={form.kind} onValueChange={(v) => setKind(v as CostChangeKind)} className="flex flex-wrap gap-x-6 gap-y-2">
                {CHANGE_KINDS.map((k) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-sm">
                    <RadioGroupItem value={k} />
                    {t(CHANGE_KIND_KEY[k])}
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
          {form.kind === 'POTENTIAL' && causeField}
          <div className="space-y-1.5">
            <Label htmlFor="cc-change-title">{t('CostControl_change_field_title')} *</Label>
            <Input id="cc-change-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} autoFocus />
          </div>
          {form.kind !== 'TRANSFER' && (
            <div className="space-y-1.5">
              <Label htmlFor="cc-change-contract">{t('CostControl_change_field_contract')}{contractOptional ? '' : ' *'}</Label>
              <Select value={form.contractId ?? NONE} onValueChange={(v) => setForm((f) => ({ ...f, contractId: v === NONE ? null : v }))}>
                <SelectTrigger id="cc-change-contract"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {contractOptional && <SelectItem value={NONE}>{t('CostControl_change_no_contract')}</SelectItem>}
                  {form.kind === 'CONTRACT_CHANGE' && !form.contractId && <SelectItem value={NONE}>{t('CostControl_change_pick_contract')}</SelectItem>}
                  {options.map((c) => <SelectItem key={c.id} value={c.id}>{c.number ? `${c.number} · ${c.name}` : c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="cc-change-reason">{t('CostControl_change_field_reason')}</Label>
            <Textarea id="cc-change-reason" rows={3} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </div>
          {form.kind !== 'POTENTIAL' && causeField}
          {touched && problem && <p className="text-sm text-destructive">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t(mode === 'create' ? 'Common_create' : 'Common_save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default ChangeDialog
